import React, { useState, useEffect, useCallback } from 'react';
import { Header } from './components/common/Header';
import { Footer } from './components/common/Footer';
import { QuickSearchModal } from './components/common/QuickSearchModal';
import { ActivityFeed } from './components/feed/ActivityFeed';
import { CompanyCatalog } from './components/catalog/CompanyCatalog';
import { TimelineView } from './components/timeline/TimelineView';
import { AdminPanel } from './components/admin/AdminPanel';
import { JobProgressToast } from './components/admin/JobProgressToast';
import { useCompanies } from './hooks/useCompanies';
import { useActivityFeed } from './hooks/useActivityFeed';
import { useUrlRouter } from './hooks/useUrlRouter';
import { PolicyType, ScrapeJob, ActiveTab } from './types';
import { trackPageView } from './services/analytics';

export const App: React.FC = () => {
  const {
    activeTab,
    companyId: selectedCompanyId,
    policyType: selectedPolicyType,
    baseRevId: selectedBaseRevId,
    targetRevId: selectedTargetRevId,
    viewMode: selectedViewMode,
    navigate,
    navigateToFeed,
    navigateToCatalog,
    navigateToAdmin,
    navigateToCompany,
  } = useUrlRouter();

  const [isSearchOpen, setIsSearchOpen] = useState<boolean>(false);
  const [activeJobs, setActiveJobs] = useState<ScrapeJob[]>([]);
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('tos_theme');
      if (saved) return saved === 'dark';
    }
    return false; // Default: Light Mode
  });

  const { companies, stats, loading, error, refreshCompanies } = useCompanies();
  const { feedItems, loading: feedLoading, error: feedError } = useActivityFeed(companies);

  // Global Keyboard Shortcuts (Cmd+K / Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen((prev) => !prev);
      }
      if (e.key === 'Escape') {
        setIsSearchOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Dark mode HTML class toggle & persistence
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('tos_theme', isDarkMode ? 'dark' : 'light');
    }
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  // Google Analytics Page View Tracking on SPA route transition
  useEffect(() => {
    let path = '/';
    let title = 'ToS & Privacy Tracker';
    if (activeTab === 'feed') {
      path = '/';
      title = 'Recent Updates | ToS & Privacy Tracker';
    } else if (activeTab === 'catalog') {
      path = '/companies';
      title = 'Tracked Companies | ToS & Privacy Tracker';
    } else if (activeTab === 'timeline') {
      path = `/company/${selectedCompanyId}/${selectedPolicyType}`;
      title = `${selectedCompanyId} Policy Timeline | ToS & Privacy Tracker`;
    } else if (activeTab === 'admin') {
      path = '/admin';
      title = 'Admin Console | ToS & Privacy Tracker';
    }
    trackPageView(path, title);
  }, [activeTab, selectedCompanyId, selectedPolicyType]);

  // Navigate to Timeline / Reader for a specific company and policy
  const handleSelectCompanyPolicy = useCallback(
    (companyId: string, policyType: PolicyType = 'terms_of_service', baseRev?: string, targetRev?: string) => {
      navigateToCompany(companyId, policyType, 'timeline', baseRev, targetRev);
    },
    [navigateToCompany]
  );

  const handleTabChange = useCallback(
    (tab: ActiveTab) => {
      if (tab === 'feed') navigateToFeed();
      else if (tab === 'catalog') navigateToCatalog();
      else if (tab === 'admin') navigateToAdmin();
      else navigate({ activeTab: tab });
    },
    [navigateToFeed, navigateToCatalog, navigateToAdmin, navigate]
  );

  const handleAddJob = useCallback((job: any) => {
    if (!job) return;
    const scrapeJob: ScrapeJob = {
      jobId: job.jobId || job.job_id || `job_${Date.now()}`,
      companyId: job.companyId || job.company_id || selectedCompanyId,
      policyType: job.policyType || job.policy_type || selectedPolicyType,
      status: job.status || 'completed',
      progress: job.progress || 100,
      message: job.message || 'Scrape operation triggered.',
      startedAt: job.startedAt || job.started_at || new Date().toISOString(),
      completedAt: job.completedAt || job.completed_at || new Date().toISOString(),
    };
    setActiveJobs((prev) => [scrapeJob, ...prev.filter((j) => j.jobId !== scrapeJob.jobId)]);
  }, [selectedCompanyId, selectedPolicyType]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 flex flex-col font-sans transition-colors duration-200">
      <Header
        activeTab={activeTab}
        setActiveTab={handleTabChange}
        onOpenSearch={() => setIsSearchOpen(true)}
        isDarkMode={isDarkMode}
        setIsDarkMode={setIsDarkMode}
        totalCompanies={stats.totalCompanies}
        totalRevisions={stats.totalRevisions}
      />

      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {activeTab === 'feed' && (
          <ActivityFeed
            feedItems={feedItems}
            loading={feedLoading}
            error={feedError}
            onSelectCompanyPolicy={handleSelectCompanyPolicy}
            onNavigateToCatalog={navigateToCatalog}
          />
        )}

        {activeTab === 'catalog' && (
          <CompanyCatalog
            companies={companies}
            loading={loading}
            error={error}
            stats={stats}
            onSelectCompany={handleSelectCompanyPolicy}
            onJobCreated={handleAddJob}
          />
        )}

        {activeTab === 'timeline' && (
          <TimelineView
            companyId={selectedCompanyId}
            policyType={selectedPolicyType}
            initialBaseRevId={selectedBaseRevId}
            initialTargetRevId={selectedTargetRevId}
            initialViewMode={selectedViewMode}
            companies={companies}
            onSelectCompany={(cid) => navigateToCompany(cid, selectedPolicyType, selectedViewMode)}
            onSelectPolicy={(ptype) => navigateToCompany(selectedCompanyId, ptype, selectedViewMode)}
            onBackToCatalog={navigateToFeed}
            onJobCreated={handleAddJob}
          />
        )}

        {activeTab === 'admin' && (
          <AdminPanel
            companies={companies}
            onRefresh={refreshCompanies}
            onJobCreated={handleAddJob}
          />
        )}
      </main>

      <Footer />

      {/* Global Quick Search Modal */}
      {isSearchOpen && (
        <QuickSearchModal
          isOpen={isSearchOpen}
          onClose={() => setIsSearchOpen(false)}
          companies={companies}
          onSelect={(companyId, policyType) => {
            setIsSearchOpen(false);
            handleSelectCompanyPolicy(companyId, policyType);
          }}
        />
      )}

      {/* Real-time Job Progress Toasts */}
      <JobProgressToast
        jobs={activeJobs}
        onDismiss={(jobId) => setActiveJobs((prev) => prev.filter((j) => j.jobId !== jobId))}
      />
    </div>
  );
};

export default App;
